import React, { useState } from "react";
import { IconDownload, IconEye } from "@tabler/icons-react";
import ColumnsFilter from "../../components/common/ColumnsFilter";
import DropdownFilter from "../../components/common/DropdownFilter";
import NormalSearch from "../../components/common/NormalSearch";
import ListView from "../../components/common/ListView";
import Pagination from "../../components/common/Pagination";
import ViewModal from "./_components/ViewModal";
import { useFetchInvoiceData } from "../../apis/invoiceApis";
import useDebounce from "../../utilities/useDebounce";

const InvoiceManagement: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit] = useState<number>(10);

  const debouncedSearchFor = useDebounce(searchFor, 500);

  const { data: invoiceData, isLoading } = useFetchInvoiceData(
    pageNo,
    limit,
    debouncedSearchFor
  );

  const openModal = (invoice?: any) => {
    setSelectedInvoice(invoice || null);
    setIsOpen(true);
  };
  const closeModal = () => {
    setSelectedInvoice(null);
    setIsOpen(false);
  };

  const handlePaginationFetch = (page: number) => setPageNo(page);

  const formattedInvoices = (invoiceData?.invoices || []).map(
    (item: any, idx: number) => {
      const formattedDate = item.date
        ? new Date(item.date).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : "-";
      const formattedDueDate = item.toDate
        ? new Date(item.toDate).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : "-";

      return {
        id: item.id || idx + 1,
        invoiceId: item.id,
        date: formattedDate,
        description: item.invoiceType || item.planName || "Invoice",
        amount: `${item.amount || 0} SAR`,
        dueDate: formattedDueDate,
        status: item.status || "Pending",
        raw: item,
      };
    }
  );

  return (
    <>
      <div className="p-4 space-y-4">
        <div>
          <div className="flex items-center">
            <div className=" bg-[#F7F6F2] flex items-center justify-between px-6 py-3 w-full rounded-tl-3xl rounded-tr-[40px] ">
              <h1 className="text-xl text-[#212121] tracking-[-0.5px] font-medium">
                Invoice Lists
              </h1>
              <div className="flex items-center gap-2">
                <ColumnsFilter columnsData={[]} />
                <DropdownFilter filterheadings={[]} />
                <NormalSearch
                  value={searchFor}
                  onChange={(e) => {
                    setSearchFor(e.target.value);
                    setPageNo(1);
                  }}
                />
              </div>
            </div>
            <div className="relative ml-5">
              <div className=" flex items-center gap-2 justify-end rounded-full">
                <button
                  className="flex items-center gap-1 p-2.5 text-sm rounded-full border border-[#FFFFFF] bg-[#F7F6F2]"
                >
                  <IconDownload stroke={1.5} size={24} />
                </button>
              </div>
            </div>
          </div>

          <div className=" bg-[#F7F6F2] rounded-r-3xl rounded-b-3xl">
            {isLoading ? (
              <div className="flex justify-center items-center py-16 text-slate-400">
                Loading invoices...
              </div>
            ) : formattedInvoices.length === 0 ? (
              <div className="flex justify-center items-center py-16 text-slate-400">
                No invoices found
              </div>
            ) : (
              <ListView
                data={formattedInvoices}
                onRowClick={(client: any) => openModal(client)}
                columns={[
                  {
                    label: "S/N",
                    render: (_: any, i: number) => (pageNo - 1) * limit + i + 1,
                  },
                  {
                    label: "Invoice Id",
                    render: (c: any) => c.invoiceId,
                  },
                  { label: "Date", render: (c: any) => c.date },
                  { label: "Description", render: (c: any) => c.description },
                  {
                    label: "Amount",
                    render: (c: any) => c.amount,
                  },
                  {
                    label: "Due Date",
                    render: (c: any) => c.dueDate,
                  },
                  {
                    label: "Status",
                    render: (c: any) => {
                      const isAssigned = c.status === "Paid" || c.status === "SUCCESS";
                      const statusClasses = isAssigned
                        ? "bg-[#20BF55]/20 border border-[#20BF55] text-[#20BF55]"
                        : "bg-[#FF4848]/20 border border-[#FF4848] text-[#FF4848]";
                      return (
                        <div className="flex justify-center">
                          <button
                            className={`rounded-full w-24 px-4 py-1  text-center ${statusClasses}`}
                          >
                            {c.status}
                          </button>
                        </div>
                      );
                    },
                  },
                  {
                    label: "Actions",
                    render: (c: any) => (
                      <div className="space-x-2 flex items-center justify-center">
                        <button
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            openModal(c);
                          }}
                          className="p-2 border rounded-full bg-[#F7F6F2] hover:bg-slate-200 transition-colors"
                        >
                          <IconEye />
                        </button>
                        <button
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            openModal(c);
                          }}
                          className="px-4 text-center py-2 border rounded-full bg-[#F7F6F2] hover:bg-slate-200 transition-colors"
                        >
                          View / Pay
                        </button>
                      </div>
                    ),
                  },
                ]}
              />
            )}

            {invoiceData?.totalPages > 1 && (
              <div className="p-4 border-t border-slate-200">
                <Pagination
                  currentPage={pageNo}
                  totalPages={invoiceData.totalPages}
                  fetchData={handlePaginationFetch}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <ViewModal open={isOpen} invoice={selectedInvoice} onClose={closeModal} />
    </>
  );
};

export default InvoiceManagement;
